import React, { useCallback, useState } from 'react';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { SectionRankDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { covertToTimeString } from '@diritto/common/utils/date';
import {
  ContentListType,
  TrackerExtraInfoProps,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { Color } from '@diritto/common/style/color';
import { parseUserAgent } from '@diritto/common/utils/userAgent';
import { useBlockProfiles } from 'state-hooks/accounts/useBlockProfiles';
import { SectionTitle } from 'app-components/home/sections/components/SectionTitle';
import { SectionSwipeWrapper } from 'app-components/home/sections/components/SectionSwipeWrapper';
import { SectionRankTextItem } from 'app-components/home/sections/SectionRankTextItem';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useMedia } from 'ui-hooks/useMediaQuery';
import { getValidContentAndProfile } from 'utils/sectionContentUtils';
import { Divider } from 'ui-components/common/Divider';
import { useInAppState } from 'contexts/InAppProvider';

type SectionProps = {
  section: SectionRankDto;
  contents: Record<number, ContentRelayDto>;
  profileLites: Record<number, ProfileLiteRelayDto>;
};

const ITEMS_PER_PAGE = 10;

export const SectionRankTexts = ({
  section,
  contents,
  profileLites,
  trackerExtraInfo,
}: SectionProps & TrackerExtraInfoProps) => {
  const { tracker } = useTrackingState();
  const blockedProfileIds = useBlockProfiles();
  const [currentPage, setCurrentPage] = useState(1);
  const { userAgent } = useInAppState();
  const { isWeb, isMobileDevice } = parseUserAgent(userAgent ?? '');
  const { isPc } = useMedia();

  let itemsPerColumn: number;
  if (typeof window === 'undefined') {
    itemsPerColumn = isWeb && !isMobileDevice ? 5 : 10;
  } else {
    itemsPerColumn = isPc ? 5 : 10;
  }

  const handleOnClickTracker = useCallback(
    (contentId: number, listItemIndex: number) => {
      tracker.track(TrackingEventType.SelectContentListItem, {
        listType: ContentListType.SectionContent,
        contentId,
        listItemIndex,
        params: { sectionId: section.id, sectionDesign: section.design, sectionTitle: section.title },
        trackerExtraInfo,
      });
    },
    [section.design, section.id, section.title, tracker, trackerExtraInfo],
  );

  const handleIndexChange = useCallback((index: number) => {
    setCurrentPage(index + 1);
  }, []);

  const validContentIds = section.contentIds.filter(contentId => {
    const result = getValidContentAndProfile(contentId, contents, profileLites, blockedProfileIds);
    return result !== null;
  });

  const totalPages = Math.ceil(validContentIds.length / ITEMS_PER_PAGE);
  const gridRowCount = Math.min(validContentIds.length, itemsPerColumn);

  const renderItem = useCallback(
    (contentId: number, index: number, listItemIndex: number, pageContentIds: number[]) => {
      const validationResult = getValidContentAndProfile(contentId, contents, profileLites, blockedProfileIds);
      if (!validationResult) {
        return null;
      }

      const { content, profile } = validationResult;
      const isLastItem = index === pageContentIds.length - 1;
      const showDivider = (index + 1) % itemsPerColumn !== 0 && !isLastItem;

      return (
        <div key={`rank-item-${content.id}`} className="min-w-0 overflow-hidden">
          <SectionRankTextItem
            content={content}
            profile={profile}
            rank={listItemIndex + 1}
            onClick={() => handleOnClickTracker(contentId, listItemIndex)}
          />
          <div className="relative h-[1px]">{showDivider && <Divider color={Color.Gray200} />}</div>
        </div>
      );
    },
    [contents, profileLites, blockedProfileIds, handleOnClickTracker, itemsPerColumn],
  );

  const renderPage = useCallback(
    (pageIndex: number) => {
      const pageStartIndex = pageIndex * ITEMS_PER_PAGE;
      const pageContentIds = validContentIds.slice(pageStartIndex, pageStartIndex + ITEMS_PER_PAGE);

      return (
        <div
          className="px-4 rounded-md grid gap-y-[10px] grid-flow-col pc:grid-cols-2 pc:gap-x-2 grid-cols-1 gap-x-0"
          style={{ gridTemplateRows: `repeat(${gridRowCount}, 1fr)` }}>
          {pageContentIds.map((contentId, index) => {
            const listItemIndex = pageStartIndex + index;
            return renderItem(contentId, index, listItemIndex, pageContentIds);
          })}
        </div>
      );
    },
    [validContentIds, gridRowCount, renderItem],
  );

  if (validContentIds.length === 0) {
    return null;
  }

  return (
    <div>
      <SectionTitle
        section={section}
        moreLink={section.moreLink}
        description={section.aggregatedAt ? `${covertToTimeString(section.aggregatedAt)} 기준` : undefined}
        trackerExtraInfo={trackerExtraInfo}
      />
      <SectionSwipeWrapper
        renderPage={renderPage}
        currentIndex={currentPage - 1}
        totalPages={totalPages}
        onIndexChange={handleIndexChange}
      />
    </div>
  );
};

export default SectionRankTexts;
